import createDOMPurify from 'dompurify';
import { assertSafeImageUrl } from '@/common/domain/utils/assertSafeImageUrl';

/**
 * The one allowlist sanitiser for stored prose HTML (LLAMS-610 X-06). Every HTML sink (prose blocks,
 * `rich_text` fields, the read-only editor fragment, the Block Library thumbnail through the shared
 * renderer) and the PDF prose path convert the output of this function, so HTML and PDF can never
 * disagree about which text or links a body carries.
 *
 * Allowed: what the prose editor emits (TipTap StarterKit + Link: p, h1-h6, ul/ol/li, blockquote,
 * pre/code, br, hr, strong/em/s and their legacy aliases, a) plus inline `u`, `span`, `div` and `img`
 * that stored legacy bodies carry. Everything else (script, style, iframe, object, embed, form, svg,
 * math, every `on*` handler, srcdoc, srcset ...) is stripped; its text content is kept.
 */

// The link editor's own scheme allow-list (@tiptap/extension-link `isAllowedUri`), none script-capable
// (kept from LLAMS-610 DB-05's `neutralizeUnsafeUrls`). Relative paths and `#fragments` carry no scheme.
const ALLOWED_SCHEMES = new Set(['http', 'https', 'ftp', 'ftps', 'mailto', 'tel', 'callto', 'sms', 'cid', 'xmpp']);
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;

/** URL parsers drop ASCII whitespace and control characters, so `java\tscript:` is still `javascript:`. */
function withoutControls(value: string): string {
  return Array.from(value)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 0x20 && (code < 0x7f || code > 0x9f);
    })
    .join('');
}

/** The schemes authors can link to in the editor; relative paths and `#fragments` carry no scheme and are allowed. */
export function isAllowedDocumentUrl(value: string): boolean {
  const scheme = SCHEME.exec(withoutControls(value))?.[1]?.toLowerCase();
  return scheme === undefined || ALLOWED_SCHEMES.has(scheme);
}

const ALLOWED_TAGS = [
  'p',
  'div',
  'span',
  'br',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'strike',
  'a',
  'img',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'colgroup',
  'col'
];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height', 'style', 'colspan', 'rowspan'];
const LINK_REL = 'noopener noreferrer nofollow';
/** Longest `data:` image source kept on an `<img>` (characters). Larger inline images render nothing. */
export const MAX_DATA_IMAGE_SOURCE_LENGTH = 1_000_000;
/** Inputs longer than this are sanitised but never memoised, so the cache cannot hold large bodies. */
export const MAX_MEMOISED_INPUT_LENGTH = 256 * 1024;

const NAMED_COLORS = new Set(
  (
    'black silver gray grey white maroon red purple fuchsia green lime olive yellow navy blue teal aqua orange ' +
    'brown pink gold crimson indigo violet cyan magenta darkred darkgreen darkblue lightgray lightgrey darkgray ' +
    'darkgrey transparent currentcolor'
  ).split(' ')
);
const NUM = String.raw`[+-]?(?:\d+\.?\d*|\.\d+)%?`;
const COLOR_FN = new RegExp(String.raw`^(?:rgba?|hsla?)\(\s*${NUM}(?:\s*,\s*${NUM}){2,3}\s*\)$`, 'i');
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const DECORATION_WORDS = new Set(['underline', 'line-through', 'overline']);

/** Explicit grammar per property; a value that does not match is dropped. */
const STYLE_GRAMMAR: Record<string, (value: string) => boolean> = {
  color: (v) => HEX_COLOR.test(v) || NAMED_COLORS.has(v.toLowerCase()) || COLOR_FN.test(v),
  'font-weight': (v) => /^(?:normal|bold|bolder|lighter|[1-9]00)$/i.test(v),
  'font-style': (v) => /^(?:normal|italic|oblique)$/i.test(v),
  'text-decoration': (v) => {
    const words = v.toLowerCase().split(/\s+/);
    return words.length > 0 && (words[0] === 'none' ? words.length === 1 : words.every((w) => DECORATION_WORDS.has(w)));
  },
  'text-align': (v) => /^(?:left|right|center|justify|start|end)$/i.test(v)
};

/** Keeps only the inline declarations the PDF path also reads (colour, weight, style, decoration, alignment). */
function safeStyle(raw: string): string {
  const kept: string[] = [];
  for (const part of raw.split(';')) {
    const idx = part.indexOf(':');
    if (idx < 0) continue;
    const prop = part.slice(0, idx).trim().toLowerCase();
    const value = part.slice(idx + 1).trim();
    if (STYLE_GRAMMAR[prop]?.(value)) kept.push(`${prop}: ${value}`);
  }
  return kept.join('; ');
}

type Purifier = ReturnType<typeof createDOMPurify>;
let purifier: Purifier | null = null;

function getPurifier(): Purifier | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (purifier) return purifier;
  const instance = createDOMPurify(window);
  if (!instance.isSupported) return null;

  instance.addHook('afterSanitizeAttributes', (node) => {
    const tag = node.nodeName.toLowerCase();
    if (node.hasAttribute('style')) {
      const next = safeStyle(node.getAttribute('style') ?? '');
      if (next) node.setAttribute('style', next);
      else node.removeAttribute('style');
    }
    if (tag === 'a') {
      const href = node.getAttribute('href');
      if (href !== null && !isAllowedDocumentUrl(href)) node.removeAttribute('href');
      if (node.hasAttribute('target') && node.getAttribute('target') !== '_blank') node.removeAttribute('target');
      if (node.hasAttribute('href')) {
        node.setAttribute('rel', LINK_REL);
      } else {
        node.removeAttribute('rel');
        node.removeAttribute('target');
      }
    } else if (tag === 'img') {
      const src = node.getAttribute('src');
      const safe = src === null ? null : assertSafeImageUrl(src);
      if (safe && !(safe.startsWith('data:') && safe.length > MAX_DATA_IMAGE_SOURCE_LENGTH)) node.setAttribute('src', safe);
      else node.removeAttribute('src');
      node.removeAttribute('target');
      node.removeAttribute('rel');
    } else {
      node.removeAttribute('href');
      node.removeAttribute('src');
      node.removeAttribute('target');
      node.removeAttribute('rel');
    }
  });
  purifier = instance;
  return instance;
}

const MEMO_LIMIT = 256;
const memo = new Map<string, string>();

const TEXT_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeText = (text: string): string => text.replace(/[&<>"']/g, (c) => TEXT_ESCAPES[c] ?? c);

/** Without a DOM nothing can be parsed or rendered safely: degrade to escaped plain text. */
function plainTextFallback(html: string): string {
  return escapeText(html.replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, '').replace(/<[^>]*>?/g, ''));
}

/**
 * Sanitises resolved prose HTML. Pure and memoised by input string, so applying it at the resolver
 * and again at the sink costs one parse per distinct body. Idempotent: the output sanitises to itself.
 */
export function sanitizeProseHtml(html: string | null | undefined): string {
  if (!html) return '';
  const cached = memo.get(html);
  if (cached !== undefined) return cached;

  const instance = getPurifier();
  const clean = instance
    ? instance.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
        ALLOW_UNKNOWN_PROTOCOLS: false,
        KEEP_CONTENT: true
      })
    : plainTextFallback(html);

  if (html.length <= MAX_MEMOISED_INPUT_LENGTH) {
    if (memo.size >= MEMO_LIMIT) memo.delete(memo.keys().next().value as string);
    memo.set(html, clean);
  }
  return clean;
}
